import styled from "styled-components";
import { connect } from "react-redux";
import { useState } from "react";
import { Check, Copy, Crown } from "@phosphor-icons/react";

import Header from "../Header";
import Footer from "../Footer";
import ContactUs from "../ContactUs";
import Login from "../Login/index";
import Reveal from "../Shared/Reveal";
import Seo from "../Shared/Seo";
import { Container, H2, Lead } from "../Shared/ui";

const PLANS = [
  { months: 6, price: "600", label: "6 months premium membership" },
  { months: 12, price: "1200", label: "12 months premium membership", featured: true },
];

const ACCOUNT = [
  { label: "Account Name", value: "VINAYAKAFOUNDATION" },
  { label: "A/C No", value: "3878000101029926", copy: true },
  { label: "IFS Code", value: "PUNB0387800", copy: true },
  { label: "Bank", value: "Punjab National Bank" },
  { label: "Branch", value: "South Bazar, Kannur - 670002" },
];

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <CopyBtn type="button" onClick={onCopy} aria-label={`Copy ${label}`} $copied={copied}>
      {copied ? <Check size={16} weight="bold" /> : <Copy size={16} />}
      <span>{copied ? "Copied" : "Copy"}</span>
    </CopyBtn>
  );
};

const Home = (props) => {
  return (
    <>
      <Seo page="premium"/>
      <Header hideRegistrationPopup = "true"/>
      <Page>
        <Container>
          <Reveal>
            <H2 as="h1">Membership Plans</H2>
            <Lead>Premium members can view contact details of verified profiles.</Lead>
          </Reveal>

          <Layout>
            <Plans>
              {PLANS.map((plan, i) => (
                <Reveal key={plan.months} index={i} className={plan.featured ? "plan featured" : "plan"}>
                  {plan.featured && <Crown size={28} weight="duotone" />}
                  <h2>{plan.months} months</h2>
                  <Price>
                    <small>Rs</small> {plan.price}
                  </Price>
                  <p>{plan.label}</p>
                </Reveal>
              ))}
            </Plans>

            <Reveal index={2}>
              <Bank>
                <h2>Account details</h2>
                <dl>
                  {ACCOUNT.map(({ label, value, copy }) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>
                        <span>{value}</span>
                        {copy && <CopyButton value={value} label={label} />}
                      </dd>
                    </div>
                  ))}
                </dl>
                <Note>
                  Contact <a href="tel:+917907300686">+91 79073 00686</a> for any queries
                </Note>
              </Bank>
            </Reveal>
          </Layout>
        </Container>
      </Page>
      <ContactUs/>
      <Footer/>
      { props.showLogin &&
        <Login/>
      }
    </>
  );
};

const Page = styled.main`
  padding: calc(var(--header-h) + clamp(40px, 6vw, 72px)) 0 clamp(24px, 4vw, 48px);
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  margin-top: clamp(32px, 4vw, 48px);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Plans = styled.div`
  display: grid;
  gap: 20px;

  .plan {
    position: relative;
    padding: 28px 32px;
    border-radius: var(--radius-card);
    border: 1px solid var(--border);
    background: var(--surface);
    box-shadow: var(--shadow);
    transition: transform 0.45s var(--ease-out), box-shadow 0.45s var(--ease-out);
  }

  .plan:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
  }

  .plan h2 {
    font-size: 20px;
    margin: 0;
  }

  .plan p {
    margin: 4px 0 0;
    color: var(--text-muted);
  }

  .featured {
    background: linear-gradient(160deg, var(--indigo) 0%, var(--navy) 100%);
    border-color: transparent;
    color: rgb(254 254 254 / 0.82);
  }

  .featured h2 {
    color: #fefefe;
  }

  .featured p {
    color: rgb(254 254 254 / 0.78);
  }

  .featured svg {
    position: absolute;
    top: 24px;
    right: 24px;
    color: var(--butter);
  }
`;

const Price = styled.div`
  font-family: var(--font-display);
  font-size: clamp(44px, 5vw, 56px);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-top: 8px;

  small {
    font-size: 0.4em;
    font-weight: 600;
    vertical-align: 0.9em;
  }

  .featured & {
    color: var(--butter);
  }
`;

const Bank = styled.div`
  padding: 32px;
  border-radius: var(--radius-card);
  background: var(--surface-butter);

  h2 {
    font-size: 22px;
    margin-bottom: 20px;
  }

  dl {
    margin: 0;
    display: grid;
    gap: 16px;
  }

  dt {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-muted);
  }

  dd {
    margin: 2px 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-weight: 700;
    font-size: 17px;
    overflow-wrap: anywhere;
  }
`;

const CopyBtn = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 14px;
  border-radius: var(--radius-pill);
  border: 1.5px solid var(--border-strong);
  background: var(--surface);
  color: ${({ $copied }) => ($copied ? "var(--coral)" : "var(--text)")};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.3s var(--ease-out);

  &:hover {
    border-color: var(--text);
  }

  &:active {
    transform: scale(0.96);
  }
`;

const Note = styled.p`
  margin: 24px 0 0;
  padding-top: 20px;
  border-top: 1px solid var(--border-strong);
  font-size: 15px;

  a {
    font-weight: 700;
  }
`;

const mapStatetoProps = (state) => {
  return {
    user: state.userState.user,
    showLogin: state.userState.showLogin
  }
}

export default connect(mapStatetoProps)(Home);
